import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { PanelOrder } from '@storeo/api-client';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { money, moneyShort, relativeTime, dateTime } from '../lib/format';
import { PlaceholderTag } from '../components/lumina/Placeholder';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Aguardando',
  paid: 'Aprovado',
  expired: 'Expirado',
  refunded: 'Reembolsado',
  chargeback: 'Chargeback',
};

const STATUS_CLS: Record<string, string> = {
  pending: 'bg-[#f59e0b]/15 text-[#f59e0b]',
  paid: 'bg-secondary-container/25 text-secondary',
  expired: 'bg-surface-variant text-outline',
  refunded: 'bg-error/15 text-error',
  chargeback: 'bg-error/15 text-error',
};

const SIZE = 20;

type Tab = { key: string; label: string; match: (s: string) => boolean };
const TABS: Tab[] = [
  { key: 'all', label: 'Todos', match: () => true },
  { key: 'paid', label: 'Aprovado', match: (s) => s === 'paid' },
  { key: 'pending', label: 'Aguardando', match: (s) => s === 'pending' },
  { key: 'expired', label: 'Expirado', match: (s) => s === 'expired' },
  { key: 'refunded', label: 'Reembolsado', match: (s) => s === 'refunded' || s === 'chargeback' },
];

/** Vendas & Pedidos — redesign Lumina (docs/prototype/storeo_vendas_pedidos).
 *  Dado real: orders + refundOrder + markShipped + salesSummary. Cards de "carrinhos
 *  recuperados"/meta/order-bump e o rail de conversão são placeholder — sem API. */
export function Vendas() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orders = useQuery({ queryKey: ['orders', page], queryFn: () => api.orders({ page, size: SIZE }) });
  const summary = useQuery({ queryKey: ['salesSummary'], queryFn: () => api.salesSummary() });

  const rows = useMemo(() => {
    const t = TABS.find((x) => x.key === tab) ?? TABS[0]!;
    return (orders.data?.items ?? []).filter((o) => t.match(o.status));
  }, [orders.data, tab]);

  const refund = useMutation({
    mutationFn: (o: PanelOrder) => api.refundOrder(o.orderId, 'refund'),
    onMutate: (o) => setBusyId(o.orderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível estornar'),
    onSettled: () => setBusyId(null),
  });
  const ship = useMutation({
    mutationFn: (o: PanelOrder) => api.markShipped(o.orderId),
    onMutate: (o) => setBusyId(o.orderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível marcar como enviado'),
    onSettled: () => setBusyId(null),
  });

  const today = summary.data?.todayTotal ?? 0;
  const count = summary.data?.todayCount ?? 0;
  const ticket = count > 0 ? today / count : 0;

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      {/* header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-space-md">
        <div>
          <div className="flex items-center gap-space-xs mb-space-3xs">
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-primary font-bold">Terminal de Operações</span>
            <span className="text-outline text-label-sm">•</span>
            <span className="inline-flex items-center gap-space-3xs font-label-sm text-label-sm text-secondary">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" />
              Tempo real
            </span>
          </div>
          <div className="flex items-baseline gap-space-sm">
            <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Gestão de Vendas &amp; Pedidos</h1>
            {count > 0 ? (
              <span className="px-space-xs py-space-3xs rounded-full bg-surface-container-high text-on-surface-variant font-label-sm text-label-sm">
                {count} pedido(s) hoje
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-space-sm">
          <div className="flex items-center rounded-xl bg-surface-container-low p-space-3xs shadow-sm">
            <button className="px-space-sm py-space-2xs rounded-lg font-label-md text-label-md bg-surface-container-highest text-on-surface transition-all">Hoje</button>
            <button className="px-space-sm py-space-2xs rounded-lg font-label-md text-label-md text-on-surface-variant hover:text-on-surface transition-all">7 Dias</button>
            <button className="px-space-sm py-space-2xs rounded-lg font-label-md text-label-md text-on-surface-variant hover:text-on-surface transition-all">30 Dias</button>
          </div>
        </div>
      </div>

      {error ? (
        <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">{error}</p>
      ) : null}

      {/* 4 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-lg">
        <Card label="Faturamento Hoje" icon="payments" wrap="bg-secondary-container/20 text-secondary" value={`R$ ${moneyShort(today)}`}>
          <span className="font-body-sm text-body-sm text-on-surface-variant">Semana: R$ {moneyShort(summary.data?.weekTotal ?? 0)}</span>
        </Card>
        <Card label="Pedidos Aprovados" icon="check_circle" wrap="bg-primary-container/20 text-primary" value={String(count)}>
          <span className="font-body-sm text-body-sm text-on-surface-variant">no dia de hoje</span>
        </Card>
        <Card label="Ticket Médio" icon="trending_up" wrap="bg-tertiary-container/30 text-tertiary" value={`R$ ${moneyShort(ticket)}`}>
          <span className="font-body-sm text-body-sm text-on-surface-variant">faturamento ÷ pedidos</span>
        </Card>
        <Card label="Carrinhos Recuperados" icon="smart_toy" wrap="bg-secondary-container/20 text-secondary" value="48" placeholder>
          <span className="font-body-sm text-body-sm text-on-surface-variant">Bot Telegram &amp; E-mail • 29,4% recuperados</span>
        </Card>
      </div>

      {/* filter bar */}
      <div className="rounded-2xl bg-surface-container-low p-space-md shadow-md flex flex-col gap-space-md">
        <div className="relative">
          <MSym name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]" />
          <input
            className="w-full h-11 pl-10 pr-space-md rounded-xl bg-surface-container-highest/60 text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container-highest transition-all"
            placeholder="Buscar por pedido, nome ou e-mail…"
            type="text"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-space-sm pt-space-xs border-t border-surface-container-highest/40">
          <div className="flex flex-wrap items-center gap-space-xs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-space-md py-space-2xs rounded-xl font-label-md text-label-md transition-all ${
                  tab === t.key ? 'bg-primary-container text-on-primary-container' : 'bg-surface-container text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['orders'] })}
            className="flex items-center gap-space-3xs text-primary hover:underline font-label-sm text-label-sm"
          >
            <MSym name="refresh" className="text-[16px]" /> Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-xl">
        {/* table */}
        <div className="xl:col-span-9 min-w-0 flex flex-col gap-space-md">
          <div className="rounded-2xl bg-surface-container overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                    <th className="py-space-md px-space-lg">ID &amp; Método</th>
                    <th className="py-space-md px-space-md">Data</th>
                    <th className="py-space-md px-space-md">Cliente</th>
                    <th className="py-space-md px-space-md">Produto</th>
                    <th className="py-space-md px-space-md text-right">Total</th>
                    <th className="py-space-md px-space-md">Status</th>
                    <th className="py-space-md px-space-lg text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="font-body-sm text-body-sm text-on-surface">
                  {orders.isLoading ? (
                    <tr>
                      <td colSpan={7} className="py-space-2xl text-center text-on-surface-variant">Carregando…</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-space-2xl text-center text-on-surface-variant">
                        {page > 0 ? 'Não há mais pedidos.' : 'Nenhuma venda nesse filtro.'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((o) => {
                      const isPix = o.paymentMethod !== 'card';
                      return (
                        <tr key={o.orderId} className="hover:bg-surface-container-high/60 transition-colors border-t border-surface-container-highest/30">
                          <td className="py-space-md px-space-lg">
                            <div className="flex items-center gap-space-xs">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPix ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'}`}>
                                <MSym name={isPix ? 'bolt' : 'credit_card'} className="text-[18px]" />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-label-md text-label-md text-on-surface font-bold">#{o.number}</span>
                                <span className={`font-label-sm text-label-sm font-medium ${isPix ? 'text-secondary' : 'text-primary'}`}>
                                  {isPix ? 'PIX' : 'Cartão'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="py-space-md px-space-md">
                            <div className="flex flex-col">
                              <span className="text-on-surface font-label-md text-label-md">{relativeTime(o.createdAt)}</span>
                              <span className="text-outline font-label-sm text-label-sm">{dateTime(o.createdAt)}</span>
                            </div>
                          </td>
                          <td className="py-space-md px-space-md">
                            <span className="font-label-md text-label-md text-on-surface font-semibold block truncate max-w-[14rem]">
                              {o.customerName || o.customerEmail}
                            </span>
                            <span className="text-outline font-label-sm text-label-sm truncate block max-w-[14rem]">{o.customerEmail}</span>
                          </td>
                          <td className="py-space-md px-space-md text-on-surface-variant">
                            <span className="block truncate max-w-[14rem]">{o.productName}</span>
                            <span className="font-label-sm text-label-sm text-outline">
                              {o.deliveryChannel === 'physical' ? 'Físico' : 'Digital'}
                            </span>
                          </td>
                          <td className="py-space-md px-space-md text-right font-label-md text-label-md font-extrabold text-on-surface whitespace-nowrap">
                            {money(o.amount)}
                          </td>
                          <td className="py-space-md px-space-md">
                            <span className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold ${STATUS_CLS[o.status] ?? 'bg-surface-variant text-outline'}`}>
                              {STATUS_LABEL[o.status] ?? o.status}
                            </span>
                          </td>
                          <td className="py-space-md px-space-lg text-right">
                            <div className="inline-flex gap-space-2xs">
                              {o.status === 'paid' && o.deliveryChannel === 'physical' ? (
                                <button
                                  className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-surface-variant text-on-surface font-label-sm text-label-sm font-semibold transition-colors disabled:opacity-50"
                                  disabled={busyId === o.orderId}
                                  onClick={() => ship.mutate(o)}
                                >
                                  Marcar enviado
                                </button>
                              ) : null}
                              {o.status === 'paid' ? (
                                <button
                                  className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-error/15 hover:text-error text-on-surface font-label-sm text-label-sm font-semibold transition-colors disabled:opacity-50"
                                  disabled={busyId === o.orderId}
                                  onClick={() => {
                                    setError(null);
                                    refund.mutate(o);
                                  }}
                                >
                                  Estornar
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              className="px-space-md py-space-xs rounded-xl bg-surface-container-low hover:bg-surface-container text-on-surface font-label-md text-label-md transition-all disabled:opacity-40"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Mais recentes
            </button>
            <span className="font-label-sm text-label-sm text-outline">Página {page + 1}</span>
            <button
              className="px-space-md py-space-xs rounded-xl bg-surface-container-low hover:bg-surface-container text-on-surface font-label-md text-label-md transition-all disabled:opacity-40"
              disabled={!orders.data?.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Mais antigos →
            </button>
          </div>
        </div>

        {/* right rail — placeholder */}
        <div className="xl:col-span-3 flex flex-col gap-space-md">
          <div className="rounded-2xl bg-surface-container p-space-lg shadow-md">
            <div className="flex items-center justify-between mb-space-sm">
              <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface font-semibold">Canais de Conversão</span>
              <PlaceholderTag />
            </div>
            <div className="space-y-space-md">
              {[
                { label: 'Link direto', v: 'R$ 14.830', pct: 55, c: 'bg-primary' },
                { label: 'Orgânico', v: 'R$ 5.100', pct: 25, c: 'bg-tertiary' },
                { label: 'Telegram Bot', v: 'R$ 6.590', pct: 15, c: 'bg-secondary' },
                { label: 'E-mail', v: 'R$ 2.370', pct: 5, c: 'bg-outline' },
              ].map((r) => (
                <div key={r.label}>
                  <div className="flex justify-between font-label-sm text-label-sm mb-1">
                    <span className="text-on-surface font-medium">{r.label}</span>
                    <span className="text-on-surface-variant">{r.v}</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-surface-container-highest overflow-hidden">
                    <div className={`h-full rounded-full ${r.c}`} style={{ width: `${r.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-space-md p-space-sm rounded-xl bg-surface-container-high flex items-center gap-space-xs font-label-sm text-label-sm text-on-surface-variant">
              <MSym name="verified_user" className="text-secondary text-[16px]" /> Split PIX automático ativo
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({
  label,
  icon,
  wrap,
  value,
  placeholder,
  children,
}: {
  label: string;
  icon: string;
  wrap: string;
  value: string;
  placeholder?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl bg-surface-container p-space-lg shadow-md flex flex-col justify-between gap-space-sm group">
      <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-primary/10 blur-xl group-hover:scale-150 transition-transform pointer-events-none" />
      <div className="flex items-center justify-between">
        <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface font-semibold flex items-center">
          {label}
          {placeholder ? <PlaceholderTag /> : null}
        </span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${wrap}`}>
          <MSym name={icon} className="text-[18px]" />
        </div>
      </div>
      <div>
        <div className="font-metric-stat text-metric-stat text-on-surface font-bold tracking-tight">{value}</div>
        <div className="mt-space-2xs">{children}</div>
      </div>
    </div>
  );
}
